import React, { useEffect, useState } from 'react';
import BaseModal from './BaseModal';
import { groupByCompany } from './formGrouping';
import { INPUT, LABEL } from './ui';

// Themed modal for starting a template — a name, and the form whose answers
// fill it in — and, with `askName={false}`, for pointing an existing one at
// another form. The company comes with the form, so there is no company
// picker: a dev's forms span every company, and are grouped by it so the
// choice reads as "which company's form".
const CreateTemplateModal = ({
  open,
  title = 'Create new template',
  message = 'Name the template and pick the form whose answers fill it in.',
  confirmLabel = 'Create',
  askName = true,
  forms = [],
  defaultFormId = null,
  onConfirm,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setFormId(String(defaultFormId ?? forms[0]?.id ?? ''));
  }, [open, defaultFormId, forms]);

  const groups = groupByCompany(forms);
  const byCompany = groups.length > 1;
  const noForms = forms.length === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if ((askName && !trimmed) || !formId) return;
    onConfirm({ name: trimmed, form_id: Number(formId) });
  };

  return (
    <BaseModal
      open={open}
      titleId="create-template-title"
      title={title}
      message={noForms ? null : message}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      confirmLabel={confirmLabel}
      showConfirm={!noForms}
      cancelLabel={noForms ? 'Close' : 'Cancel'}
      bodyClassName="px-6 py-4 space-y-4"
    >
      {noForms ? (
        <p className="text-sm text-gray-300">
          A template is filled in from a form’s answers, so it needs a form to start from. Create
          one on the Forms tab first.
        </p>
      ) : (
        <>
          {askName && (
            <div>
              <label htmlFor="ct-name" className={LABEL}>Name</label>
              <input
                id="ct-name"
                type="text"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Roof Inspection Report"
                className={INPUT}
              />
            </div>
          )}
          <div>
            <label htmlFor="ct-form" className={LABEL}>Filled in from</label>
            <select
              id="ct-form"
              value={formId}
              onChange={(e) => setFormId(e.target.value)}
              className={INPUT}
              required
            >
              {byCompany
                ? groups.map((group) => (
                  <optgroup key={group.key} label={group.companyName}>
                    {group.items.map((form) => (
                      <option key={form.id} value={form.id}>{form.name}</option>
                    ))}
                  </optgroup>
                ))
                : forms.map((form) => (
                  <option key={form.id} value={form.id}>{form.name}</option>
                ))}
            </select>
          </div>
        </>
      )}
    </BaseModal>
  );
};

export default CreateTemplateModal;
